import React, { useState, useEffect, useMemo } from 'react';
import {useDispatch, useSelector} from "react-redux";
import PreviewTable from "./components/PreviewTable";
import {sectorTargetFillPortfolio } from "../model.js";
import {equalFillPortfolio, proRataFillPortfolio} from "../../../lib/weightSchemes";
import { Divider, Progress, Row, Col, Steps, Space, Typography, Tooltip } from 'antd';
import { prepareGroupedBarData, aggregateMarketCaps, aggregatePriceStats } from "./utils";
import {WeightSchemeNav, ToggleGroupedBars} from "./components/TabBars";
import NivoBarChart from "../../../charts/nivo/NivoBarChart";
import openNotification from "../../../utils/openNotification";
import {selectAllTickerAttrs} from "../../../Store/slices/tickers.slice";
import {fetchTickerData} from "../../../Api/tickersApi";
import {money, stringMixin} from "../../../utils/strings";
import TickerGroupComparison from "./TickerGroupComparison";
import NivoBullet from "../../../charts/nivo/NivoBullet";
import {InfoCircleFilled} from "@ant-design/icons";


const ReductionSteps = ({ counts }) => {
    return (
        <Steps current={counts.length-1} progressDot direction="horizontal">
            {counts.map((count, index) => (
                <Steps.Step key={index} title={count.title} subTitle={count.description} />

            ))}
        </Steps>
    );
};



/**
 * Preview component for the SMA Lab
 * @param subset
 * @param outSet
 * @param consolidatedList
 * @param setView
 * @param config
 * @param updateConfig
 * @param setSubset
 * @param counts - Array of objects with title and description
 * @param onSave - Function to save the current subset
 * @returns {JSX.Element}
 * @constructor
 */
const Preview = ({subset, outSet, consolidatedList, setView, config, updateConfig, setSubset, counts,
                 onSave }) => {
    const dispatch = useDispatch();
    const [localSubset, setLocalSubset] = useState(subset);
    const [displayField, setDisplayField] = useState('wgt');
    const [weightOption, setWeightOption] = useState(config?.weightScheme ?? 'equal');
    const [marketCapsChartData, setMarketCapsChartData] = useState(undefined);
    const [priceStats, setPriceStats] = useState(undefined);
    const allTickers = useMemo(() => consolidatedList.map(item => item.symbol), [subset]);
    const tickerAddtionalData = useSelector(state => selectAllTickerAttrs(state, allTickers, [
        'metrics.mktcap', 'stats.summary.px'
    ]));

    useEffect(() => {
        if (allTickers.length > 0) {
            dispatch(fetchTickerData({ symbols: allTickers, fields: [
                    'symbol',
                    'metrics.mktcap',
                    'stats.summary.px'
                ] }));
        }
    }, [allTickers, dispatch]); // Now correctly depends on subsetTickers

    useEffect(() => {
        if (!tickerAddtionalData || tickerAddtionalData.length === 0) return; // Don't update if no market cap data
        const updatedConsolidatedList = consolidatedList.map(item => {
            return {
                ...item,
                mktcap: tickerAddtionalData?.[item.symbol]?.metrics?.mktcap??0,
                px: tickerAddtionalData?.[item.symbol]?.stats?.summary?.px??0
            };
        });
        const { subsetMarketCap, totalMarketCap } = aggregateMarketCaps(updatedConsolidatedList, localSubset);
        const { maxPrice, avgPrice, maxPriceWeight } = aggregatePriceStats(updatedConsolidatedList, localSubset);
        // This check avoids infinite render problems
        if (!marketCapsChartData || subsetMarketCap!==marketCapsChartData[0]) {
            setMarketCapsChartData([subsetMarketCap, totalMarketCap]);
            setPriceStats([maxPrice, avgPrice, maxPriceWeight]);
        }
    }, [tickerAddtionalData]);

    const handleWeightOptionChange = (option) => {
        const weightFunction = {
            equal: equalFillPortfolio,
            proRata: proRataFillPortfolio,
            sectorRefill: sectorTargetFillPortfolio
        }[option];
        let updatedSubset;
        if (option!=='proRata'){
            updatedSubset = weightFunction(subset, outSet)
        }
        else {
            updatedSubset = weightFunction(subset, config.maxPerHolding * 0.01 || 0);
        }
        setLocalSubset(updatedSubset);
        setWeightOption(option);
        setSubset(updatedSubset);
        updateConfig('weightScheme', option);
        const tickersAdded = updatedSubset.length - subset.length;
        if (tickersAdded > 0){
            openNotification({
                title: "IA Sector Refill",
                description: `Tickers added ${tickersAdded}.`
            });
        }
    };

    const originalWeights = consolidatedList.reduce((acc, item) => ({ ...acc, [item.symbol]: item.wgt }), {});
    const ratioKept = localSubset.length / consolidatedList.length;

    const ColumnContainer = ({title, children}) => (
        <Space direction={'vertical'}>
            <Typography.Text strong>{title}</Typography.Text>
            {children}
        </Space>
    )

    return (
        <div>
            {marketCapsChartData && (
                <Row align={'middle'} justify={'start'} style={{paddingLeft: '50px', paddingTop: '30px', paddingBottom: '30px'}}>
                    <Col>
                        <Space direction={'vertical'}>
                            <div className={'px-6'}>
                                <Typography.Title level={5}>Stock SMA Reduction Steps</Typography.Title>
                            </div>
                            <ReductionSteps counts={counts}/>
                        </Space>
                    </Col>
                    <Col offset={1}>
                        <Row align={'middle'} justify={'center'} gutter={32}>
                            <Col>
                                <Progress size={[200, 15]}
                                          trailColor={'#ddd'}
                                          type="line"
                                          percent={(marketCapsChartData[0] / marketCapsChartData[1]) * 100}
                                          format={percent => <span style={{color: '#000'}}>
                                              <b>{stringMixin.largeNumber(marketCapsChartData[0], 0)} / {stringMixin.largeNumber(marketCapsChartData[1], 0)}</b> Covered ($)
                                          </span>
                                }
                                          percentPosition={{type: 'outer', align: 'center'}}
                                          strokeColor={{
                                              from: '#0B67F6',
                                              to: '#0B67F6'
                                          }}
                                          style={{
                                              width:200
                                          }}
                                />
                            </Col>
                            <Col>
                                <Progress size={[95, 95]}
                                          trailColor={'#ddd'}
                                          type="dashboard"
                                          percent={Math.round(ratioKept * 100)} format={percent =>
                                            <Typography.Title level={4} style={{ display: 'flex', flexDirection: 'column' }}>
                                                {percent}%
                                                <Typography.Text style={{ fontSize: '11px', flexShrink: 0 }}>Tickers Kept</Typography.Text>
                                            </Typography.Title>
                                          }
                                          strokeColor={{
                                              '0%': '#0869FF',
                                              '80%': '#0869FF',
                                              '100%': '#E70D1D'
                                          }}
                                          strokeWidth={7}

                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}
            <Divider/>
            <div>
                <Row wrap={false}>
                    <Col flex={'0 0 600px'} style={{paddingTop: 150}}>
                        <ToggleGroupedBars setDisplayField={setDisplayField} setView={setView} />
                        <div style={{height: 800}}>
                            <NivoBarChart
                                data={prepareGroupedBarData(localSubset, consolidatedList, displayField)}
                                keys={['Full Set', 'Subset']}
                                leftAxisLabel={''}
                                bottomAxisLabel={displayField==='wgt'?'Weight (%)':'Number of Tickers'}
                                rotateLabels={0}
                                paddingBottom={100}
                                paddingLeft={120}
                                vertical={false}
                                barPadding={0.1}

                            />
                        </div>
                    </Col>
                    <Col flex={'1 1 800px'}>
                        <PreviewTable
                            subset={localSubset}
                            originalWeights={originalWeights}
                            tickerAddtionalData={tickerAddtionalData}
                            onSave={onSave}
                            buttonGroup={<WeightSchemeNav
                                    onWeightOptionChange={handleWeightOptionChange}
                                    currentWeightOption={config?.weightScheme ?? 'equal'}
                                />}
                        />
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Preview;